<script setup lang="ts">
import maplibregl, { Popup } from 'maplibre-gl'
import { storeToRefs } from 'pinia'
import {
  inject,
  onMounted,
  onUnmounted,
  ref,
  shallowRef,
  watch,
  type Ref,
  type ShallowRef,
} from 'vue'

import { getPOIIcon, getPoiColor } from '@/services/poiService'
import { isTouchDevice } from '@/services/uiService'
import { useMobileBatchSelectionStore } from '@/stores/mobileBatchSelectionStore'
import { useUiStore } from '@/stores/uiStore'
import type { WorkspacePOI } from '@/types/poi'
import { getCreatorByline } from '@/utils/creatorByline'
import { hideMapHoverPopup, showMapHoverPopup } from '@/utils/mapHoverPopup'
import { createMapMarker, createMapMarkerElement } from '@/utils/mapMarkerElement'
import { escapeHtml } from '@/utils/trackHoverPopup'

const props = defineProps<{
  pois: WorkspacePOI[]
  selectedPoiId?: string | null
}>()

const emit = defineEmits<{
  'poi-edit': [clientId: string]
  'poi-context-menu': [detail: { poiId: string; clientX: number; clientY: number }]
}>()

const uiStore = useUiStore()
const mobileBatchSelectionStore = useMobileBatchSelectionStore()
const { hoveredWaypointId, isTrackDrawingActive } = storeToRefs(uiStore)
const mapInstance = inject<ShallowRef<maplibregl.Map | null>>('mapInstance')
const mapLoaded = inject<Ref<boolean>>('mapLoaded')
const mapStyleRevision = inject<Ref<number>>('mapStyleRevision', ref(0))

type SimpleMarker = {
  remove: () => void
  getElement: () => HTMLElement
}

const markers = ref<Array<{ id: string; marker: SimpleMarker }>>([])
const markerBodies = new Map<string, HTMLElement>()
const poiByHoverId = new Map<string, WorkspacePOI>()
const popup = shallowRef<Popup | null>(null)
const currentPopupId = ref<string | null>(null)
const TOUCH_LONG_PRESS_MOVE_TOLERANCE_PX = 12
let longPressTimer: ReturnType<typeof setTimeout> | null = null
let longPressActivated = false
let longPressStartPoint: { x: number; y: number } | null = null

const shouldRenderPOIs = () =>
  uiStore.arePOIsVisible && !uiStore.areMapElementsForceHidden && !uiStore.isInWorkspacesOverview

const removeMarkers = () => {
  markers.value.forEach(({ marker }) => marker.remove())
  markers.value = []
  markerBodies.clear()
  poiByHoverId.clear()
}

const closePopup = () => {
  popup.value = hideMapHoverPopup(popup.value)
  currentPopupId.value = null
}

const showPopup = (poi: WorkspacePOI) => {
  const map = mapInstance?.value
  if (!map) return

  const poiHoverId = `poi_${poi.clientId}`
  if (currentPopupId.value === poiHoverId) return

  const creatorByline = getCreatorByline(poi)
  const creatorBylineHtml = creatorByline
    ? `<span style="flex-shrink:0;color:#6b7280;font-size:12px;font-weight:400;">${escapeHtml(creatorByline)}</span>`
    : ''

  currentPopupId.value = poiHoverId
  popup.value = showMapHoverPopup({
    map,
    popup: popup.value,
    lngLat: [poi.lon, poi.lat],
    html:
      `<span class="font-semibold text-sm" style="display:flex;align-items:baseline;gap:4px;max-width:260px;white-space:nowrap;">` +
      `<span style="min-width:0;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(poi.name || 'POI')}</span>` +
      creatorBylineHtml +
      `</span>`,
    className: 'waypoint-popup',
    offset: 22,
  })
}

const cancelLongPress = () => {
  if (longPressTimer !== null) {
    clearTimeout(longPressTimer)
    longPressTimer = null
  }
  longPressStartPoint = null
}

const getTouchPoint = (event: TouchEvent) => {
  const touch = event.changedTouches?.[0] ?? event.touches?.[0] ?? null
  return touch ? { x: touch.clientX, y: touch.clientY } : null
}

const isWithinLongPressMoveTolerance = (point: { x: number; y: number } | null) => {
  if (!longPressStartPoint || !point) return true
  const dx = point.x - longPressStartPoint.x
  const dy = point.y - longPressStartPoint.y
  return (
    dx * dx + dy * dy <= TOUCH_LONG_PRESS_MOVE_TOLERANCE_PX * TOUCH_LONG_PRESS_MOVE_TOLERANCE_PX
  )
}

const emitPOIContextMenu = (poi: WorkspacePOI, event: MouseEvent | PointerEvent | TouchEvent) => {
  event.preventDefault()
  event.stopPropagation()
  let clientX: number | null = null
  let clientY: number | null = null
  if ('clientX' in event && typeof event.clientX === 'number') {
    clientX = event.clientX
    clientY = event.clientY
  } else if ('changedTouches' in event) {
    const touch = event.changedTouches?.[0] ?? event.touches?.[0] ?? null
    clientX = touch?.clientX ?? null
    clientY = touch?.clientY ?? null
  }
  if (clientX === null || clientY === null) return

  uiStore.setHoveredWaypoint(`poi_${poi.clientId}`)

  emit('poi-context-menu', {
    poiId: poi.clientId,
    clientX,
    clientY,
  })
}

const getSelectedHoverId = () => (props.selectedPoiId ? `poi_${props.selectedPoiId}` : null)

const updateMarkerHoverState = () => {
  const isBatchSelectionSurface =
    mobileBatchSelectionStore.activeSurface === 'collection-detail' ||
    mobileBatchSelectionStore.activeSurface === 'pinned'
  const hoveredId = isBatchSelectionSurface ? null : hoveredWaypointId.value
  const selectedId = isBatchSelectionSurface ? null : getSelectedHoverId()
  for (const [poiHoverId, body] of markerBodies.entries()) {
    const poiId = poiHoverId.startsWith('poi_') ? poiHoverId.slice(4) : poiHoverId
    const isBatchSelected = isBatchSelectionSurface && mobileBatchSelectionStore.pois.has(poiId)
    body.style.transition = 'none'
    body.style.transform =
      hoveredId === poiHoverId || selectedId === poiHoverId || isBatchSelected
        ? 'scale(1.18)'
        : 'scale(1)'
  }
}

const renderMarkers = () => {
  const map = mapInstance?.value
  if (!map || !mapLoaded?.value) return

  removeMarkers()
  closePopup()

  if (!shouldRenderPOIs()) return

  for (const poi of props.pois) {
    if (uiStore.isPOIHidden(poi.clientId)) continue
    if (!Number.isFinite(poi.lon) || !Number.isFinite(poi.lat)) continue

    // Saved POIs use the same colour the route.camp basemap uses for that category (via getPoiColor),
    // so a saved POI marker matches the basemap symbol it came from. Shares the marker DOM with route
    // waypoints (createMapMarkerElement) — only icon/colour/container class differ.
    const { element, markerEl: markerBody } = createMapMarkerElement(
      getPOIIcon(poi.type),
      getPoiColor(poi.type),
      'workspace-poi-marker-container',
    )
    const marker = createMapMarker({ element })
      .setLngLat([poi.lon, poi.lat])
      .addTo(map) as unknown as SimpleMarker

    const poiHoverId = `poi_${poi.clientId}`
    if (markerBody) markerBodies.set(poiHoverId, markerBody)
    poiByHoverId.set(poiHoverId, poi)
    element.addEventListener('mouseenter', () => {
      if (isTrackDrawingActive.value) return
      uiStore.setHoveredWaypoint(poiHoverId)
    })
    element.addEventListener('mouseleave', () => {
      if (hoveredWaypointId.value === poiHoverId && props.selectedPoiId !== poi.clientId) {
        uiStore.setHoveredWaypoint(null)
      }
    })
    element.addEventListener('click', (event) => {
      event.stopPropagation()
      if (isTrackDrawingActive.value) return
      if (
        mobileBatchSelectionStore.activeSurface === 'collection-detail' ||
        mobileBatchSelectionStore.activeSurface === 'pinned'
      ) {
        mobileBatchSelectionStore.toggle('pois', poi.clientId)
        return
      }
      if (isTouchDevice()) {
        if (longPressActivated) {
          longPressActivated = false
          return
        }
        emit('poi-edit', poi.clientId)
        return
      }
      emit('poi-edit', poi.clientId)
    })
    element.addEventListener('touchstart', (event) => {
      if (isTrackDrawingActive.value) return
      longPressActivated = false
      cancelLongPress()
      longPressStartPoint = getTouchPoint(event)
      longPressTimer = setTimeout(() => {
        longPressTimer = null
        longPressActivated = true
        emitPOIContextMenu(poi, event)
      }, 500)
    })
    element.addEventListener('touchmove', (event) => {
      if (isWithinLongPressMoveTolerance(getTouchPoint(event))) return
      cancelLongPress()
    })
    element.addEventListener('touchend', cancelLongPress)
    element.addEventListener('contextmenu', (event) => {
      if (isTrackDrawingActive.value) return
      emitPOIContextMenu(poi, event)
    })

    markers.value.push({ id: poi.clientId, marker })
  }

  updateMarkerHoverState()

  const selectedPoi = props.selectedPoiId
    ? (props.pois.find((poi) => poi.clientId === props.selectedPoiId) ?? null)
    : null
  if (selectedPoi && !isTouchDevice()) {
    showPopup(selectedPoi)
  }
}

watch(
  () => [
    props.pois,
    uiStore.arePOIsVisible,
    Array.from(uiStore.hiddenPOIIds.values()).sort().join('|'),
    uiStore.areMapElementsForceHidden,
    uiStore.isInWorkspacesOverview,
    mapStyleRevision?.value,
  ],
  renderMarkers,
  { deep: true },
)

watch(mapLoaded ?? ref(false), renderMarkers)

watch(hoveredWaypointId, (newId, oldId) => {
  updateMarkerHoverState()

  if (oldId?.startsWith('poi_') && oldId !== getSelectedHoverId()) {
    closePopup()
  }

  if (!newId?.startsWith('poi_') || isTouchDevice()) return

  const poi = poiByHoverId.get(newId)
  if (poi) showPopup(poi)
})

watch(
  () => props.selectedPoiId ?? null,
  (selectedPoiId, previousSelectedPoiId) => {
    updateMarkerHoverState()

    if (previousSelectedPoiId && previousSelectedPoiId !== selectedPoiId) {
      closePopup()
    }

    if (!selectedPoiId || isTouchDevice()) return

    const poi = poiByHoverId.get(`poi_${selectedPoiId}`)
    if (poi) showPopup(poi)
  },
  { immediate: true },
)

watch(
  () => [
    mobileBatchSelectionStore.activeSurface,
    [...mobileBatchSelectionStore.pois].sort().join('|'),
  ],
  () => {
    updateMarkerHoverState()
  },
)

onMounted(renderMarkers)

onUnmounted(() => {
  cancelLongPress()
  removeMarkers()
  closePopup()
})
</script>

<template>
  <div style="display: none" />
</template>
